/**
 * Role-based notification category filtering utility
 * Maps user roles to their allowed notification categories
 */

import { NotificationCategory } from 'stores/users/users-store';

export interface RoleNotificationMapping {
  [roleName: string]: string[]; // Role name -> Array of allowed category names
}

/**
 * Mapping of roles to their allowed notification categories
 * Based on business requirements provided
 */
export const ROLE_NOTIFICATION_CATEGORIES: RoleNotificationMapping = {
  Admin: ['Request and Event', 'TNA Forms', 'SSA Forms', 'IWS Forms'],
  Planner: [
    // TFT Planner
    'Request and Event',
    'Feedback Forms'
  ],
  'Planner MTC': ['Request and Event', 'Feedback Forms'],
  'Global Engineering': ['Request and Event', 'Feedback Forms'],
  'Global Manufacturing': ['Request and Event', 'Feedback Forms'],
  'Regional Manufacturing VP': ['Request and Event', 'Feedback Forms'],
  PoC: ['Request and Event', 'TNA Forms', 'SSA Forms', 'IWS Forms', 'Feedback Forms'],
  'Factory Lead': ['Request and Event', 'Feedback Forms'],
  Reporting: ['Request and Event'],
  Instructor: ['Request and Event', 'Feedback Forms', 'Instructor & Master Data'],
  Employee: ['Request and Event', 'TNA Forms', 'SSA Forms', 'IWS Forms', 'Theoretical Forms', 'Feedback Forms']
};

/**
 * Get allowed notification category names for given roles
 * @param userRoles Array of role names the user has
 * @returns Array of unique category names the user can access
 */
export function getAllowedNotificationCategories(userRoles: string[]): string[] {
  if (!userRoles || userRoles.length === 0) {
    return [];
  }

  // Collect all allowed categories from all user roles
  const allowedCategories = new Set<string>();

  userRoles.forEach(role => {
    const roleCategories = ROLE_NOTIFICATION_CATEGORIES[role];
    if (roleCategories) {
      roleCategories.forEach(category => allowedCategories.add(category));
    }
  });

  return Array.from(allowedCategories);
}

/**
 * Get all available notification categories with default state
 * @param defaultSelected Default selection state for all categories
 * @returns Complete array of all notification categories
 */
export function getAllNotificationCategories(defaultSelected: boolean = false): NotificationCategory[] {
  return [
    {
      id: 1,
      name: 'Request and Event',
      notificationTypes: [40, 50, 570, 70],
      isSelected: defaultSelected
    },
    {
      id: 2,
      name: 'TNA Forms',
      notificationTypes: [130, 140, 150, 240],
      isSelected: defaultSelected
    },
    {
      id: 3,
      name: 'SSA Forms',
      notificationTypes: [310, 320, 330],
      isSelected: defaultSelected
    },
    {
      id: 4,
      name: 'IWS Forms',
      notificationTypes: [410, 430, 440, 470, 480, 490],
      isSelected: defaultSelected
    },
    {
      id: 5,
      name: 'Theoretical Forms',
      notificationTypes: [250],
      isSelected: defaultSelected
    },
    {
      id: 6,
      name: 'Feedback Forms',
      notificationTypes: [160, 170],
      isSelected: defaultSelected
    },
    {
      id: 7,
      name: 'Instructor & Master Data',
      notificationTypes: [510],
      isSelected: defaultSelected
    }
  ];
}

/**
 * Filter notification categories based on user roles
 * @param allCategories Complete list of notification categories
 * @param userRoles Array of role names the user has
 * @returns Filtered array of categories the user can access
 */
export function filterNotificationCategoriesByRoles<T extends { name: string }>(allCategories: T[], userRoles: string[]): T[] {
  if (!userRoles || userRoles.length === 0) {
    return [];
  }

  const allowedCategoryNames = getAllowedNotificationCategories(userRoles);

  return allCategories.filter(category => allowedCategoryNames.includes(category.name));
}

/**
 * Get notification categories filtered by roles with proper selection state
 * @param userRoles Array of role names the user has
 * @param existingCategories Existing categories with their selection state (optional)
 * @param defaultSelected Default selection state for new categories
 * @returns Filtered categories maintaining existing selection state where possible
 */
export function getNotificationCategoriesForRoles(
  userRoles: string[],
  existingCategories: NotificationCategory[] = [],
  defaultSelected: boolean = false
): NotificationCategory[] {
  if (!userRoles || userRoles.length === 0) {
    return [];
  }

  const allCategories = getAllNotificationCategories(defaultSelected);
  const filteredCategories = filterNotificationCategoriesByRoles(allCategories, userRoles);

  // Maintain existing selection state where possible
  return filteredCategories.map(category => {
    const existingCategory = existingCategories.find(existing => existing.id === category.id);
    return existingCategory ? { ...category, isSelected: existingCategory.isSelected } : category;
  });
}
